import { useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, X, Check, Loader2, Image, Film, Mic, Square, Circle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import axios from "axios";

const API = "/api";

const ACCEPTED_TYPES = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "video/mp4": "video",
  "video/quicktime": "video",
  "audio/mpeg": "audio",
  "audio/wav": "audio",
  "audio/mp3": "audio",
  "audio/webm": "audio",
};

export default function SubmitPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    role: "",
    why: "",
    city: "",
    country: "",
    email: "",
    whatsapp: "",
    anonymous: false,
    website: "",
  });
  const [isSeeking, setIsSeeking] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 50MB.");
      return;
    }

    const mediaType = ACCEPTED_TYPES[file.type];
    if (!mediaType) {
      toast.error("Unsupported file type. Please upload JPG, PNG, MP4, MOV, MP3, or WAV.");
      return;
    }

    setMediaFile(file);

    if (mediaType === "image") {
      const reader = new FileReader();
      reader.onload = (ev) => setMediaPreview({ type: "image", url: ev.target.result });
      reader.readAsDataURL(file);
    } else if (mediaType === "video") {
      setMediaPreview({ type: "video", url: URL.createObjectURL(file) });
    } else {
      setMediaPreview({ type: "audio", url: URL.createObjectURL(file) });
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    if (fileRef.current) fileRef.current.value = "";
    stopRecording();
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], "voice-note.webm", { type: "audio/webm" });
        setMediaFile(file);
        setMediaPreview({ type: "audio", url: URL.createObjectURL(blob) });
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      toast.error("Could not access microphone. Please check permissions.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    clearInterval(timerRef.current);
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const uploadToCloudinary = async (file) => {
    const mediaType = ACCEPTED_TYPES[file.type];
    const resourceType = mediaType === "audio" ? "raw" : mediaType;

    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature`, {
        params: { resource_type: resourceType, folder: "iambecause" },
      });
      const sig = sigRes.data;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        formData,
        {
          onUploadProgress: (p) => {
            if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
          },
        }
      );

      return { url: uploadRes.data.secure_url, type: mediaType };
    } catch (err) {
      console.error("Cloudinary upload error:", err);
      throw new Error("Media upload failed. Please try again.");
    }
  };

  const [transcribing, setTranscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isAudio = mediaFile && ACCEPTED_TYPES[mediaFile.type] === "audio";
    // For audio submissions, why text is optional (will be transcribed)
    if (!form.name.trim() || !form.role.trim() || (!form.why.trim() && !isAudio)) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const hasEmail = form.email.trim() && form.email.includes("@");
    const hasWhatsapp = (form.whatsapp || "").replace(/\D/g, "").length >= 8;
    if (!hasEmail && !hasWhatsapp) {
      toast.error("please share your email or whatsapp — one way for your story to reach you.");
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      let mediaUrl = null;
      let mediaType = null;
      let whyText = form.why;

      if (mediaFile) {
        const result = await uploadToCloudinary(mediaFile);
        mediaUrl = result.url;
        mediaType = result.type;

        // Auto-transcribe audio
        if (result.type === "audio" && !form.why.trim()) {
          setTranscribing(true);
          toast.info("Transcribing your voice...");
          try {
            const transRes = await axios.post(`${API}/transcribe?audio_url=${encodeURIComponent(result.url)}`);
            whyText = transRes.data.transcript;
            mediaType = "voice"; // Mark as voice-transcribed
          } catch {
            toast.error("Couldn't transcribe your recording. Please type your why instead.");
            setSubmitting(false);
            setTranscribing(false);
            return;
          } finally {
            setTranscribing(false);
          }
        }
      }

      await axios.post(`${API}/stories`, {
        name: form.name,
        role: form.role,
        why: whyText,
        media_url: mediaUrl,
        media_type: mediaType,
        city: form.city || null,
        country: form.country || null,
        email: form.email || null,
        whatsapp: form.whatsapp || null,
        anonymous: form.anonymous,
        website: form.website || "",
        stage: isSeeking ? "seeking" : null,
      });

      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="submit-success" className="min-h-[80vh] flex items-center justify-center px-6">
        <Toaster position="top-center" richColors />
        <div className="text-center max-w-md opacity-0 animate-scale-in" style={{ animationFillMode: 'forwards' }}>
          <div className="w-16 h-16 rounded-full bg-[#E91E8C]/10 flex items-center justify-center mx-auto mb-6">
            <Check size={28} className="text-[#E91E8C]" />
          </div>
          <h1 className="text-3xl font-medium text-[#16130F] mb-3">
            Thank you.
          </h1>
          <p className="font-sans text-lg text-[#4B5563] leading-relaxed mb-8">
            Your story has been submitted and will be reviewed shortly. Every story matters.
          </p>
          <button
            data-testid="back-to-stories-btn"
            onClick={() => navigate("/")}
            className="btn-amber"
          >
            Back to Stories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-page" className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="bg-[#FBF7F0] py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold text-[#E91E8C] mb-4">
            share your why
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ fontFamily: 'var(--genz-font-display)', fontWeight: 800, color: '#0F0F0F' }}>
            why do you do what you do?
          </h1>
          <p className="mt-4 text-base md:text-lg font-sans text-[#4B5563] leading-relaxed">
            take your time. no word limit. say what's true.
          </p>
          <p className="mt-2 hand-note text-xl" style={{ color: '#E91E8C' }}>
            still figuring it out? that counts too. share where you are right now.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Seeking Toggle */}
          <div data-testid="seeking-toggle" className="flex items-center gap-1 p-1 rounded-full bg-[#FBF7F0] border border-[#E5E7EB] w-fit">
            <button
              type="button"
              data-testid="toggle-know-why"
              onClick={() => setIsSeeking(false)}
              className={`px-4 py-2 rounded-full text-sm font-sans font-medium transition-all ${!isSeeking ? "bg-[#E91E8C] text-white shadow-sm" : "text-[#4B5563] hover:text-[#16130F]"}`}
            >
              I know my why
            </button>
            <button
              type="button"
              data-testid="toggle-still-searching"
              onClick={() => setIsSeeking(true)}
              className={`px-4 py-2 rounded-full text-sm font-sans font-medium transition-all ${isSeeking ? "bg-[#E91E8C] text-white shadow-sm" : "text-[#4B5563] hover:text-[#16130F]"}`}
            >
              I'm still searching
            </button>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Full Name <span className="text-[#E91E8C]">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              data-testid="input-name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
              required
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              What do you do <span className="text-[#E91E8C]">*</span>
            </Label>
            <Input
              id="role"
              name="role"
              data-testid="input-role"
              value={form.role}
              onChange={handleChange}
              placeholder={isSeeking ? "What you do right now, even if it doesn't feel like the right thing" : "Your title or craft"}
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
              required
            />
          </div>

          {/* Why */}
          <div className="space-y-2">
            <Label htmlFor="why" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Why do you do what you do <span className="text-[#E91E8C]">*</span>
            </Label>
            {mediaFile && ACCEPTED_TYPES[mediaFile.type] === "audio" && !form.why.trim() && (
              <p className="text-xs font-sans" style={{ color: '#E91E8C' }}>
                <Mic size={10} className="inline mr-1" />
                Mira will transcribe your voice recording automatically
              </p>
            )}
            <Textarea
              id="why"
              name="why"
              data-testid="input-why"
              value={form.why}
              onChange={handleChange}
              placeholder={isSeeking ? "Tell us where you are right now. What you're feeling. What you're looking for. The searching is also a story worth telling." : "Take your time. There is no word limit."}
              rows={8}
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] text-base font-sans leading-relaxed resize-y"
              required
            />
          </div>

          {/* Media Upload */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Photo, Video, or Voice Note
              <span className="text-[#4B5563]/50 normal-case tracking-normal ml-2 font-normal">(optional)</span>
            </Label>

            {mediaPreview ? (
              <div data-testid="media-preview" className="relative rounded-xl border border-[#E5E7EB] overflow-hidden">
                {mediaPreview.type === "image" && (
                  <img src={mediaPreview.url} alt="Preview" className="w-full max-h-64 object-cover" />
                )}
                {mediaPreview.type === "video" && (
                  <video src={mediaPreview.url} controls className="w-full max-h-64" />
                )}
                {mediaPreview.type === "audio" && (
                  <div className="p-6 bg-[#FBF7F0] flex items-center gap-4">
                    <Mic size={24} className="text-[#E91E8C]" />
                    <audio src={mediaPreview.url} controls className="flex-1" />
                  </div>
                )}
                <button
                  type="button"
                  data-testid="remove-media-btn"
                  onClick={removeMedia}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Upload file */}
                <button
                  type="button"
                  data-testid="upload-media-btn"
                  onClick={() => fileRef.current?.click()}
                  className="w-full rounded-xl border-2 border-dashed border-[#E5E7EB] hover:border-[#E91E8C]/40 p-6 transition-colors text-center group"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-2 text-[#4B5563]/60 group-hover:text-[#E91E8C]">
                      <Image size={20} />
                      <Film size={20} />
                    </div>
                    <p className="text-sm font-sans font-medium text-[#4B5563]">
                      Upload a photo or video
                    </p>
                    <p className="text-xs text-[#4B5563]/60">
                      JPG, PNG, MP4, MOV up to 50MB
                    </p>
                  </div>
                </button>

                {/* OR divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                  <span className="text-xs uppercase tracking-[0.15em] font-sans text-[#4B5563]/50">or</span>
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                </div>

                {/* Record voice */}
                {recording ? (
                  <button
                    type="button"
                    data-testid="stop-recording-btn"
                    onClick={stopRecording}
                    className="w-full rounded-xl border-2 border-red-300 bg-red-50 p-6 transition-colors text-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
                          <Square size={16} className="text-white" fill="white" />
                        </div>
                      </div>
                      <p className="text-sm font-sans font-semibold text-red-600">
                        Recording... {formatTime(recordingTime)}
                      </p>
                      <p className="text-xs text-red-500/70">Tap to stop</p>
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="record-voice-btn"
                    onClick={startRecording}
                    className="w-full rounded-xl border-2 border-dashed border-[#E91E8C]/30 bg-[#E91E8C]/5 hover:border-[#E91E8C]/50 hover:bg-[#E91E8C]/10 p-6 transition-colors text-center group"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20 transition-colors">
                        <Mic size={20} className="text-[#E91E8C]" />
                      </div>
                      <p className="text-sm font-sans font-medium text-[#E91E8C]">
                        Record your voice
                      </p>
                      <p className="text-xs text-[#4B5563]/60">
                        Say your why out loud. Sometimes the voice tells the story better.
                      </p>
                    </div>
                  </button>
                )}
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.mp3,.wav"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* City & Country */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
                City
              </Label>
              <Input
                id="city"
                name="city"
                data-testid="input-city"
                value={form.city}
                onChange={handleChange}
                placeholder="Your city"
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
                Country
              </Label>
              <Input
                id="country"
                name="country"
                data-testid="input-country"
                value={form.country}
                onChange={handleChange}
                placeholder="Your country"
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
              />
            </div>
          </div>

          {/* Contact — email or whatsapp required */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Email
              <span className="text-[#E91E8C] normal-case tracking-normal ml-2 font-normal italic">so Mira can reach you with your Mirror</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
            />
            <p className="text-xs font-sans italic" style={{ color: '#888780' }}>
              Used only to send you your Mirror. Nothing else. Ever.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatsapp" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              WhatsApp
              <span className="text-[#E91E8C] normal-case tracking-normal ml-2 font-normal italic">if you'd rather not share email</span>
            </Label>
            <Input
              id="whatsapp"
              name="whatsapp"
              type="tel"
              data-testid="input-whatsapp"
              value={form.whatsapp}
              onChange={handleChange}
              placeholder="+91 98765 43210"
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#E91E8C] h-12 text-base"
            />
            <p className="text-xs font-sans font-semibold" style={{ color: '#E91E8C' }}>
              we need one of the two — email or whatsapp — so your story stays connected to you. never public, never shared.
            </p>
          </div>

          {/* Anonymous option */}
          <label data-testid="anonymous-checkbox" className="flex items-start gap-3 cursor-pointer group py-2">
            <input
              type="checkbox"
              name="anonymous"
              checked={form.anonymous}
              onChange={handleChange}
              className="mt-1 accent-[#E91E8C] w-4 h-4 rounded"
            />
            <div>
              <span className="text-sm font-sans font-medium text-[#16130F] group-hover:text-[#E91E8C] transition-colors">
                Share my story anonymously
              </span>
              <p className="text-xs text-[#4B5563]/70 mt-0.5">
                Your name will be hidden. Your story will appear as &ldquo;A {form.role || "storyteller"}{form.city ? ` \u00b7 ${form.city}` : ""}&rdquo;
              </p>
            </div>
          </label>

          {/* Consent checkbox */}
          <label data-testid="consent-checkbox" className="flex items-start gap-3 cursor-pointer group py-2">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 accent-[#E91E8C] w-4 h-4 rounded"
            />
            <div>
              <span className="text-sm font-sans text-[#4B5563] leading-relaxed">
                I agree to share my story on iambecause.com. I understand it will be published as I wrote it, may be shared on social media, and can be removed at any time by writing to hello@iambecause.com.
              </span>
              <p className="text-xs mt-1">
                <Link to="/privacy" className="text-[#E91E8C] hover:underline">Privacy Policy</Link>
                <span className="text-[#4B5563]/50 mx-1.5">&middot;</span>
                <Link to="/terms" className="text-[#E91E8C] hover:underline">Terms of Use</Link>
              </p>
            </div>
          </label>

          {/* Upload progress */}
          {submitting && uploadProgress > 0 && uploadProgress < 100 && (
            <div data-testid="upload-progress" className="w-full bg-[#E5E7EB] rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-[#E91E8C] rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          {/* Honeypot - hidden from humans */}
          <div className="absolute -left-[9999px]" aria-hidden="true" tabIndex={-1}>
            <input name="website" value={form.website} onChange={handleChange} autoComplete="off" tabIndex={-1} />
          </div>

          {/* Submit */}
          <button
            type="submit"
            data-testid="submit-story-btn"
            disabled={submitting || !consent}
            className="btn-amber w-full !py-4 text-base"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {transcribing ? "Transcribing your voice..." : uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : "Submitting..."}
              </>
            ) : (
              "Submit Your Story"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
